import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { diffWords, type DiffToken } from '@/lib/wordDiff';

export interface BeforeAfterDiffProps {
  before: string | null;
  after: string | null;
  /**
   * 'stacked' = Original durchgestrichen ueber dem Neuen (Hausformat).
   * 'columns' = nebeneinander, aber NUR ab md. Darunter faellt die
   * Komponente immer auf 'stacked' zurueck, weil gestapelte Spalten einen
   * ganzen Textblock zwischen die beiden Fassungen schieben und den
   * Vergleich damit unmoeglich machen.
   * 'heldMitFussnote' = der NEUE Text zuerst und groesser in der Verlaufbox,
   * das Original als durchgestrichene Fussnote darunter (Entwurfs-Schalter
   * `titelAnordnung`, Richtung A der Feinschliff-Mockups). Diff-Marker und
   * `cutAt` bleiben erhalten; ohne `before` entfaellt die Fussnote.
   * 'inline' = EIN Textlauf, in dem alle Token stehen - kein Vorher-Block,
   * kein Nachher-Kasten, keine Fussnote (Entwurfs-Schalter `titelAnordnung`,
   * dritter Wert). `cutAt` gilt NUR fuer den Nachher-Anteil und wird in
   * dieser Variante deshalb ignoriert (siehe die Prop dort).
   */
  variant?: 'stacked' | 'columns' | 'heldMitFussnote' | 'inline';
  /** Feldname ueber dem Paar, z.B. "Titel". */
  label?: ReactNode;
  /**
   * Wird RECHTS neben dem Label gerendert, z.B. der "Anpassen"-Knopf.
   *
   * Eigene Prop statt das Label ganz aus der Komponente zu ziehen: die
   * Alternative haette alle vier fremden Aufrufer beruehrt (FeedCheckInsights
   * zweimal, SuccessScoreboard, Phase2Texte). Opt-in, ohne Wirkung, wenn kein
   * `label` gesetzt ist - eine Handlung neben einem nicht vorhandenen Feld
   * waere kontextlos.
   */
  labelSlot?: ReactNode;
  /** Blendet die Diff-Markierung aus, etwa im Druck. */
  plain?: boolean;
  /**
   * Kleine VORHER/NACHHER-Captions ueber den beiden Bloecken. Opt-in: im
   * Erfolgs-Modal ("Beispiel-Optimierung") stand das Titel-Paar ohne jede
   * Kennzeichnung — welcher der alte und welcher der neue ist, verriet nur
   * die Durchstreichung. Werkbank & Co. bleiben ohne die Prop unveraendert.
   */
  sideLabels?: boolean;
  /**
   * Setzt einen Kuerzungsstrich im NACHHER-Block an dieser Position, gerechnet
   * in CODEPUNKTEN. Alles dahinter bleibt sichtbar, gedaempft auf 38 Prozent.
   *
   * Opt-in mit `undefined` als Vorbelegung: die vier fremden Aufrufer
   * (FeedCheckInsights zweimal, SuccessScoreboard, Phase2Texte) sehen keine
   * Aenderung.
   *
   * Ein ueberkreuzendes Token wird in ZWEI TEILE DESSELBEN ELEMENTTYPS
   * geteilt - ein <ins> wird zu zwei <ins>. Genau das war der Einwand, mit dem
   * Entscheidung E5 den Strich unter den Diff verwiesen hat ("wuerde die
   * Diff-Markierung zerreissen"); geteilt statt zerschnitten bleibt sie
   * vollstaendig erhalten.
   *
   * In `variant="inline"` OHNE WIRKUNG: der Nachher-Kasten, dessen Anteil der
   * Schnitt meint, gibt es in dieser Variante nicht. Bewusst dokumentiert statt
   * still geschluckt - ein Aufrufer, der beides gleichzeitig setzt, soll das
   * hier lesen koennen statt es aus dem Verhalten zu erraten.
   */
  cutAt?: number | null;
  /**
   * Ton der ENTFERNTEN Woerter. `farbe` (Vorbelegung) ist das heutige
   * Rot-auf-Rot, `neutral` laesst Flaeche und Schriftfarbe weg und behaelt das
   * <del>.
   *
   * Das <del> bleibt, weil es die Aussage fuer Screenreader traegt. Weg ist
   * allein die Flaeche - und dann MUSS der Aufrufer einen sichtbaren
   * Ersatztraeger stellen (WCAG 1.4.1). In der Werkbank ist das der Zaehler im
   * Feld-Label; die beiden werden nie in getrennten Commits ausgeliefert.
   */
  removedTone?: 'farbe' | 'neutral';
  /**
   * Ton der ERGAENZTEN Woerter. `gruen` (Vorbelegung) ist das heutige
   * Gruen-auf-Gruen, `neutral` laesst Flaeche und Farbe weg.
   *
   * Den dritten Ton `violett` gibt es seit S3 nicht mehr: D39 hat seinen
   * einzigen Produzenten gestrichen, den Entwurfs-Schalter `insMarke` in
   * EntwurfWerkbank.tsx, und der KI-Ton der App ist seit dem Petrol-Umbau
   * ohnehin `primary` (siehe AttributeChips/ChangeSummary). Der Kasten
   * darunter ist heute die flache Flaeche card plus primary/5 (D38, siehe
   * `karteEntlastenContrast.test.ts`); Messgrundlage frueherer Werte war der
   * ALTE Verlaufskasten card plus primary/8 als Verlaufsanfang und violet/8
   * als Verlaufsende, den es nicht mehr gibt. Historisch GEMESSEN am alten
   * Kasten (src/test/contrast.ts, Schrift 14 px, also AA_NORMAL): violett
   * riss AA im hellen Thema mit
   * 3,92:1 - ein Grund mehr, den Ton nicht wieder einzufuehren.
   */
  addedTone?: 'gruen' | 'neutral';
}

/**
 * Teilt einen Tokenstrom an einer Codepunkt-Position.
 *
 * Gerechnet wird ueber `Array.from`, nicht ueber `slice`: `slice` trennt
 * zusammengesetzte Zeichen mitten in der UTF-16-Kodierung und erzeugt ein
 * halbes Zeichen. Dieselbe Regel wie in `splitAtCut`.
 *
 * Liegt der Schnitt hinter dem Ende, kommt der ganze Strom als `davor` zurueck
 * und `dahinter` ist leer - der Aufrufer zeichnet dann keinen Strich.
 */
export function splitTokensAt(
  tokens: DiffToken[],
  cut: number,
): { davor: DiffToken[]; dahinter: DiffToken[] } {
  const davor: DiffToken[] = [];
  const dahinter: DiffToken[] = [];
  let gezaehlt = 0;
  for (const token of tokens) {
    const punkte = Array.from(token.text);
    if (gezaehlt >= cut) {
      dahinter.push(token);
      continue;
    }
    if (gezaehlt + punkte.length <= cut) {
      davor.push(token);
      gezaehlt += punkte.length;
      continue;
    }
    // Das ueberkreuzende Token: beide Haelften behalten `op`, also denselben
    // Elementtyp. Das ist der ganze Punkt.
    const rest = cut - gezaehlt;
    davor.push({ ...token, text: punkte.slice(0, rest).join('') });
    dahinter.push({ ...token, text: punkte.slice(rest).join('') });
    gezaehlt = cut;
  }
  return { davor, dahinter };
}

function Tokens({
  tokens,
  side,
  plain,
  removedTone = 'farbe',
  addedTone = 'gruen',
}: {
  tokens: DiffToken[];
  /**
   * 'before'/'after' filtern den Strom wie bisher (nur die eigene Seite).
   * 'inline' laesst ALLE Token durch - der ganze Punkt der Variante ist ein
   * einziger Textlauf, in dem `equal`, `removed` und `added` nebeneinander
   * stehen.
   */
  side: 'before' | 'after' | 'inline';
  plain?: boolean;
  removedTone?: 'farbe' | 'neutral';
  addedTone?: 'gruen' | 'neutral';
}) {
  return (
    <>
      {tokens
        .filter((t) =>
          side === 'inline' ? true : side === 'before' ? t.op !== 'added' : t.op !== 'removed',
        )
        .map((t, i) => {
          if (plain || t.op === 'equal') return <span key={i}>{t.text}</span>;
          // Semantische Markierung fuer Screenreader: <del> fuer entfernte, <ins>
          // fuer ergaenzte Woerter (statt reinem <span> mit nur Farbstyling).
          // <del> erbt hier bewusst die UA-Default-line-through, die deckt sich
          // mit dem line-through des umgebenden Vorher-Blocks (keine Optik-Aenderung).
          // <ins> bekommt "no-underline", weil ihr UA-Default-Underline im
          // Nachher-Block sonst neu waere und die bestehende Optik veraendern wuerde.
          if (t.op === 'removed') {
            return (
              <del
                key={i}
                data-diff="removed"
                className={
                  removedTone === 'neutral'
                    ? ''
                    : 'bg-destructive/10 text-destructive-ink rounded-sm px-0.5'
                }
              >
                {t.text}
              </del>
            );
          }
          const addedClass =
            addedTone === 'neutral'
              ? 'no-underline font-medium'
              : 'no-underline bg-success/15 text-success-ink rounded-sm px-0.5 font-medium';
          return (
            <ins key={i} data-diff="added" className={addedClass}>
              {t.text}
            </ins>
          );
        })}
    </>
  );
}

/**
 * `whitespace-pre-wrap` auf beiden Bloecken: Beschreibungen aus dem Feed tragen
 * echte Absaetze. Ohne die Regel klappt der Browser jeden Zeilenumbruch zu
 * einem Leerzeichen zusammen, und der Vorher/Nachher-Vergleich zeigt zwei
 * Textwuesten, deren Gliederung sich nicht mehr vergleichen laesst.
 */
export function BeforeAfterDiff({
  before,
  after,
  variant = 'stacked',
  label,
  labelSlot,
  plain,
  sideLabels,
  cutAt,
  removedTone,
  addedTone,
}: BeforeAfterDiffProps) {
  const { t } = useTranslation('common');
  // Gleiche Muted-Caption-Stilmittel wie das Feld-Label oben, nur eine Stufe
  // kleiner — die Captions gehoeren zum Paar, nicht ueber es.
  const sideLabelClass = 'text-[10px] font-medium uppercase tracking-wider text-muted-foreground';
  const tokens = useMemo(() => diffWords(before ?? '', after ?? ''), [before, after]);

  if (!before && !after) return null;

  // Label und labelSlot (z.B. der "Anpassen"-Knopf) stehen in EINER Zeile,
  // rechts ausgerichtet nebeneinander - dieselbe Zeile fuer alle drei
  // Rueckgabepfade, damit der Knopf nicht in einem Zweig vergessen wird.
  const labelRow =
    label || labelSlot ? (
      <div className="flex items-baseline justify-between gap-2">
        {label && (
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {label}
          </p>
        )}
        {labelSlot}
      </div>
    ) : null;

  // Inline: EIN Textlauf mit allen Token, kein Vorher-Block, kein
  // Nachher-Kasten, keine Fussnote. `cutAt` gilt nur fuer den Nachher-Anteil
  // eines getrennten Kastens und wird hier deshalb NICHT ausgewertet -
  // dokumentiert an der Prop, nicht still ignoriert.
  if (variant === 'inline') {
    return (
      <div className="space-y-1.5">
        {labelRow}
        <p className="whitespace-pre-wrap text-sm leading-relaxed">
          <Tokens tokens={tokens} side="inline" plain={plain} removedTone={removedTone} addedTone={addedTone} />
        </p>
      </div>
    );
  }

  const beforeBlock = before ? (
    <p
      aria-label={t('beforeAfter.originalAriaLabel')}
      className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground line-through decoration-muted-foreground/40"
    >
      <Tokens tokens={tokens} side="before" plain={plain} removedTone={removedTone} addedTone={addedTone} />
    </p>
  ) : (
    <p data-testid="before-empty" className="text-sm italic text-muted-foreground">
      {t('beforeAfter.emptyBefore')}
    </p>
  );

  // Der Schnitt gilt NUR fuer den Nachher-Block. Im alten Titel waere er
  // sinnlos, der geht nicht mehr zu Google.
  const nachTokens = tokens.filter((tk) => tk.op !== 'removed');
  const gesamtPunkte = nachTokens.reduce((n, tk) => n + Array.from(tk.text).length, 0);
  const geteilt =
    typeof cutAt === 'number' && cutAt > 0 && gesamtPunkte > cutAt
      ? splitTokensAt(nachTokens, cutAt)
      : null;

  const afterInhalt = geteilt ? (
    <>
      <Tokens tokens={geteilt.davor} side="after" plain={plain} removedTone={removedTone} addedTone={addedTone} />
      {/* Der Strich ist aria-hidden, der gedaempfte Rest dahinter NICHT: ein
          Screenreader liest den ganzen Titel. Die Kuerzung ist eine
          Aussage ueber die Darstellung beim Kunden, nicht ueber den
          Inhalt. */}
      <span
        data-testid="diff-cut-slash"
        aria-hidden="true"
        className="mx-0.5 font-bold text-warning-ink"
      >
        |
      </span>
      <span data-testid="diff-cut-hidden" className="opacity-[0.38]">
        <Tokens tokens={geteilt.dahinter} side="after" plain={plain} removedTone={removedTone} addedTone={addedTone} />
      </span>
    </>
  ) : (
    <Tokens tokens={tokens} side="after" plain={plain} removedTone={removedTone} addedTone={addedTone} />
  );

  const afterBlock = after ? (
    <p className="whitespace-pre-wrap text-sm leading-relaxed">{afterInhalt}</p>
  ) : (
    <p data-testid="after-empty" className="text-sm italic text-muted-foreground">
      {t('beforeAfter.emptyAfter')}
    </p>
  );

  // Held-mit-Fussnote: eigener Rueckgabepfad, weil die Reihenfolge und die
  // Schriftgroesse des Nachher-Blocks abweichen. Der Diff bleibt derselbe.
  if (variant === 'heldMitFussnote') {
    return (
      <div className="space-y-1.5">
        {labelRow}
        {/* Der alte Verlauf primary/8 auf violet/8 war seit S2 ein Verlauf
            zwischen zwei nahezu gleichen Petrol-Alphas, also ein Verlauf
            ohne Verlauf; `bg-primary/5` ist die flache Fassung nach D38. */}
        <div className="rounded-lg bg-primary/5 border border-primary/12 p-3">
          {after ? (
            <p className="whitespace-pre-wrap text-base leading-relaxed font-medium">{afterInhalt}</p>
          ) : (
            afterBlock
          )}
        </div>
        {before && (
          <p data-testid="original-fussnote" className="text-xs leading-snug text-muted-foreground">
            {t('beforeAfter.originalPrefix')}{' '}
            <span
              aria-label={t('beforeAfter.originalAriaLabel')}
              className="line-through decoration-muted-foreground/40"
            >
              <Tokens tokens={tokens} side="before" plain={plain} removedTone={removedTone} addedTone={addedTone} />
            </span>
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {labelRow}
      <div className={variant === 'columns' ? 'md:grid md:grid-cols-2 md:gap-4 space-y-2 md:space-y-0' : 'space-y-1.5'}>
        <div>
          {sideLabels && <p className={sideLabelClass}>{t('beforeAfter.beforeLabel')}</p>}
          {beforeBlock}
        </div>
        <div
          className={
            variant === 'columns'
              ? ''
              : // Der alte Verlauf primary/8 auf violet/8 war seit S2 ein
                // Verlauf zwischen zwei nahezu gleichen Petrol-Alphas, also
                // ein Verlauf ohne Verlauf; `bg-primary/5` ist die flache
                // Fassung nach D38.
                'rounded-lg bg-primary/5 border border-primary/12 p-2.5'
          }
        >
          {sideLabels && <p className={`${sideLabelClass} mb-0.5`}>{t('beforeAfter.afterLabel')}</p>}
          {afterBlock}
        </div>
      </div>
    </div>
  );
}
